/**
 * Pagination — 페이지 네비게이션
 *
 * 페이지 번호와 이전/다음 버튼으로 페이지를 전환한다. tokens.css 변수 사용.
 */

import { cn } from './cn'
import styles from './Pagination.module.css'

interface PaginationProps {
  /** 현재 페이지 (1-based) */
  current: number
  /** 전체 페이지 수 */
  total: number
  /** 페이지 변경 콜백 */
  onChange: (page: number) => void
  /** 현재 페이지 양쪽에 표시할 페이지 수 */
  siblings?: number
  /** 크기 */
  size?: 'sm' | 'md'
  className?: string
}

function getPageRange(current: number, total: number, siblings: number): (number | '...')[] {
  const range: (number | '...')[] = []

  const left = Math.max(2, current - siblings)
  const right = Math.min(total - 1, current + siblings)

  range.push(1)

  if (left > 2) {
    range.push('...')
  }

  for (let i = left; i <= right; i++) {
    range.push(i)
  }

  if (right < total - 1) {
    range.push('...')
  }

  if (total > 1) {
    range.push(total)
  }

  return range
}

export function Pagination({
  current,
  total,
  onChange,
  siblings = 1,
  size = 'md',
  className,
}: PaginationProps) {
  if (total <= 1) return null

  const pages = getPageRange(current, total, siblings)

  return (
    <nav className={cn(styles.root, styles[size], className)}>
      <button
        className={cn(styles.button, current === 1 && styles.disabled)}
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        aria-label="이전 페이지"
      >
        ‹
      </button>

      {pages.map((page, index) =>
        page === '...' ? (
          <span key={`ellipsis-${index}`} className={styles.ellipsis}>
            …
          </span>
        ) : (
          <button
            key={page}
            className={cn(styles.button, page === current && styles.active)}
            onClick={() => onChange(page)}
            aria-current={page === current ? 'page' : undefined}
          >
            {page}
          </button>
        ),
      )}

      <button
        className={cn(styles.button, current === total && styles.disabled)}
        onClick={() => current < total && onChange(current + 1)}
        disabled={current === total}
        aria-label="다음 페이지"
      >
        ›
      </button>
    </nav>
  )
}
